/**
 * dashboardApi.js – All API calls used by the Dashboard page.
 *
 * Backend documentation: docs/DASHBOARD_API.md
 *
 * Every call is routed through apiFetch() which:
 *  - Attaches the Bearer token automatically
 *  - Switches to mockHandler when REACT_APP_USE_MOCK_API=true
 */

import { apiFetch } from "./apiClient";

// ─── Mock helpers ────────────────────────────────────────────────────────────

const FARM_NAMES = [
  "ایستگاه تحقیقاتی",
  "هفت تپه",
  "امام خمینی (ره)",
  "امیر کبیر",
  "میرزا کوچک خان",
  "دعبل خزاعی",
  "سلمان فارسی",
];

const rand = (min, max, decimals = 2) =>
  parseFloat((Math.random() * (max - min) + min).toFixed(decimals));

const mockOverallStats = () => ({
  humidity: rand(55, 80, 1),           // %
  evapotranspiration: rand(3, 7, 1),   // mm/day
  rainfall: rand(5, 25, 1),            // mm
  temperature: rand(18, 38, 1),        // °C
  soil_moisture: rand(40, 90, 1),      // %
  water_stress: rand(0.1, 0.6, 2),     // index 0–1
  leaf_nitrogen: rand(1.5, 4.5, 1),    // g/kg
});

const mockComparisonChart = (indicator = "NDVI") =>
  FARM_NAMES.map((name) => ({ name, [indicator]: rand(0.4, 0.9, 3) }));

const mockDoubleBarChart = (ind1 = "NDVI", ind2 = "NDWI") =>
  FARM_NAMES.map((name) => ({
    name,
    [ind1]: rand(0.4, 0.9, 3),
    [ind2]: rand(0.3, 0.75, 3),
  }));

const mockTable = () =>
  FARM_NAMES.map((name) => ({
    name,
    ndvi: rand(0.5, 0.9, 2),
    ndwi: rand(0.3, 0.8, 2),
    lai: rand(2, 5, 1),
    humidity: rand(55, 80, 1),
    stress: rand(0.1, 0.45, 2),
  }));

const mockTimeSeries = (indicator = "NDVI") => {
  const months = [
    "فروردین", "اردیبهشت", "خرداد", "تیر",
    "مرداد", "شهریور", "مهر", "آبان",
    "آذر", "دی", "بهمن", "اسفند",
  ];
  return months.map((month, i) => ({
    month,
    value: rand(0.35 + i * 0.02, 0.55 + i * 0.02, 3),
  }));
};

// ─── API Functions ────────────────────────────────────────────────────────────

/**
 * 1. Overall statistics for the stats-card section.
 *    GET /api/dashboard/stats/
 *    Query params: date_from?, date_to?, keshte?, type?, unit?, croptype?, variety?, age?, status?
 */
export async function fetchDashboardStats(filters = {}) {
  const params = buildParams(filters);
  return apiFetch(`/api/dashboard/stats/?${params}`, {
    method: "GET",
    mockHandler: () => mockOverallStats(),
  });
}

/**
 * 2. Single-indicator comparison bar chart data (one bar per farm).
 *    GET /api/dashboard/comparison/
 *    Query params: indicator (required), date_from?, date_to?, + spatial filters
 *    Response: [{ name: string, value: number }]
 */
export async function fetchComparisonChart(indicator, filters = {}) {
  const params = buildParams({ indicator, ...filters });
  return apiFetch(`/api/dashboard/comparison/?${params}`, {
    method: "GET",
    mockHandler: () => mockComparisonChart(indicator),
  });
}

/**
 * 3. Double indicator comparison bar chart (two bars per farm).
 *    GET /api/dashboard/double-comparison/
 *    Query params: indicator1, indicator2, date_from?, date_to?, + spatial filters
 *    Response: [{ name: string, [indicator1]: number, [indicator2]: number }]
 */
export async function fetchDoubleComparisonChart(indicator1, indicator2, filters = {}) {
  const params = buildParams({ indicator1, indicator2, ...filters });
  return apiFetch(`/api/dashboard/double-comparison/?${params}`, {
    method: "GET",
    mockHandler: () => mockDoubleBarChart(indicator1, indicator2),
  });
}

/**
 * 4. Table data – multi-indicator values per farm.
 *    GET /api/dashboard/table/
 *    Query params: indicators (comma-separated), date_from?, date_to?, + spatial filters
 *    Response: [{ name, ndvi, lai, humidity, stress }]
 */
export async function fetchTableData(filters = {}) {
  const params = buildParams(filters);
  return apiFetch(`/api/dashboard/table/?${params}`, {
    method: "GET",
    mockHandler: () => mockTable(),
  });
}

/**
 * 5. Time series chart for a single indicator (monthly averages).
 *    GET /api/dashboard/timeseries/
 *    Query params: indicator (required), date_from?, date_to?, + spatial filters
 *    Response: [{ month: string, value: number }]
 */
export async function fetchDashboardTimeSeries(indicator, filters = {}) {
  const params = buildParams({ indicator, ...filters });
  return apiFetch(`/api/dashboard/timeseries/?${params}`, {
    method: "GET",
    mockHandler: () => mockTimeSeries(indicator),
  });
}

// ─── Utility ──────────────────────────────────────────────────────────────────

/**
 * Builds a URLSearchParams string from a filter object.
 * Skips empty/null/undefined values.
 */
function buildParams(obj) {
  const params = new URLSearchParams();
  Object.entries(obj).forEach(([key, val]) => {
    if (val !== undefined && val !== null && val !== "") {
      params.set(key, String(val));
    }
  });
  return params.toString();
}
